import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  message,
  Row,
  Col,
  Divider,
  Tag,
  Table,
  Avatar,
  Modal,
  Form,
  DatePicker,
  Select,
  Alert,
  Descriptions,
  Popconfirm,
  Input,
  Tooltip,
  Switch,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  SwapOutlined,
  CalendarOutlined,
  PrinterOutlined,
  SendOutlined,
  DeleteOutlined,
  UserOutlined,
  CarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Estimate } from '../types';
import { estimateApi } from '../services/api';
import PDFConfigModal from './PDFConfigModal';
import SendEstimateModal from './Estimate/SendEstimateModal';
import SMSConversationThread from './SMS/SMSConversationThread';
import PartsCatalogButton from './PartsCatalogButton';
import { ActivityLogComponent } from './ActivityLog/ActivityLog';
import { useNotifications } from '../contexts/NotificationContext';
import { socketService } from '../services/socketService';
import { AvailabilityPicker } from './Appointments/AvailabilityPicker';
import { AppointmentModal, EnhancedAppointmentModal } from './Appointments';
import { SmartAppointmentBooking } from './Appointments/SmartAppointmentBooking';
import { appointmentApi, AvailableTimeSlot } from '../services/appointmentApi';
import { copyToClipboard } from '../utils/clipboard';
import dayjs from 'dayjs';
import { calculateEstimateTotals } from '../utils/estimateTotals';
import './EstimateDetail.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const EstimateDetail: React.FC = () => {
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [pdfConfigModalVisible, setPdfConfigModalVisible] = useState(false);
  const [sendEstimateModalVisible, setSendEstimateModalVisible] = useState(false);
  const [editingDiscounts, setEditingDiscounts] = useState<{ [key: string]: boolean }>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [convertForm] = Form.useForm();
  const [selectedAppointmentSlot, setSelectedAppointmentSlot] = useState<AvailableTimeSlot | null>(null);
  const [appointmentModalVisible, setAppointmentModalVisible] = useState(false);
  const [smartBookingModalVisible, setSmartBookingModalVisible] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState<any>(null);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { subscribeToEntity, unsubscribeFromEntity } = useNotifications();

  // Edit guard: prevent WebSocket updates from resetting form state during editing
  const isEditingRef = useRef(false);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startEditing = useCallback(() => {
    isEditingRef.current = true;
  }, []);

  const stopEditing = useCallback(() => {
    isEditingRef.current = false;
    // Apply any pending updates now that editing is done
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
      if (id) fetchEstimate(parseInt(id));
    }
  }, [id]);

  // Determine route prefix based on current path
  const getRoutePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    if (location.pathname.startsWith('/technician')) return '/technician';
    if (location.pathname.startsWith('/portal')) return '/portal';
    return '/admin';
  };

  const routePrefix = getRoutePrefix();

  useEffect(() => {
    if (id) {
      fetchEstimate(parseInt(id));
      fetchExistingAppointment(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Real-time updates subscription
  useEffect(() => {
    if (!id) return;

    // Subscribe to this estimate for real-time updates
    subscribeToEntity('estimate', parseInt(id));
    console.log(`🔔 Subscribed to estimate ${id} for real-time updates`);

    // Listen for entity updates (guarded by editing state)
    const handleEntityUpdate = (data: any) => {
      console.log('🔄 Estimate updated in real-time:', data);

      if (isEditingRef.current) {
        // User is editing — queue the update instead of refetching
        if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = setTimeout(() => {
          if (!isEditingRef.current && id) {
            fetchEstimate(parseInt(id));
          }
        }, 2000);
        return;
      }

      // Not editing — refresh immediately
      if (id) {
        fetchEstimate(parseInt(id));
      }

      // Show a subtle notification
      message.info({
        content: 'Estimate updated',
        duration: 2,
        style: { marginTop: '60px' },
      });
    };

    socketService.on('entity_updated', handleEntityUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribeFromEntity('estimate', parseInt(id));
      socketService.off('entity_updated', handleEntityUpdate);
      if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
      console.log(`🔕 Unsubscribed from estimate ${id}`);
    };
  }, [id, subscribeToEntity, unsubscribeFromEntity]);

  const fetchEstimate = async (estimateId: number) => {
    try {
      setLoading(true);
      const response = await estimateApi.getById(estimateId);
      setEstimate(response.data);
    } catch (error) {
      console.error('Error fetching estimate:', error);
      message.error('Failed to fetch estimate details');
      navigate(`${routePrefix}/estimates`);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAppointment = async (estimateId: number) => {
    try {
      const appointments = await appointmentApi.getAll({ estimate_id: estimateId });
      // Find non-cancelled appointment for this estimate
      const appointment = appointments.find(apt => apt.status !== 'cancelled');
      setExistingAppointment(appointment || null);
    } catch (error) {
      console.error('Error fetching appointment:', error);
      // Don't show error message - appointment might not exist
    }
  };

  const fetchUsers = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return '#6b7280';
      case 'sent': return '#3b82f6';
      case 'approved': return '#10b981';
      case 'declined': return '#ef4444';
      case 'converted': return '#8b5cf6';
      case 'unapproved': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return <EditOutlined />;
      case 'sent': return <ClockCircleOutlined />;
      case 'approved': return <CheckCircleOutlined />;
      case 'declined': return <StopOutlined />;
      case 'converted': return <SwapOutlined />;
      case 'unapproved': return <ExclamationCircleOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent to Customer';
      case 'approved': return 'Approved by Customer';
      case 'declined': return 'Declined by Customer';
      case 'converted': return 'Converted to Work Order';
      case 'unapproved': return 'Unapproved';
      default: return status;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      // Mock API call
      message.success(`Estimate status updated to ${newStatus}`);
      if (estimate) {
        setEstimate({ ...estimate, status: newStatus as any });
      }
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleConvertToWorkOrder = async () => {
    try {
      const values = await convertForm.validateFields();

      // Validate appointment slot is selected
      if (!selectedAppointmentSlot) {
        message.error('Please select an appointment time slot');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      // First, convert estimate to work order
      const response = await fetch(`${API_BASE_URL}/admin/estimates/${estimate?.id}/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          priority: values.priority,
          estimated_completion: values.estimated_completion?.format('YYYY-MM-DD HH:mm:ss'),
          assigned_advisor: values.assigned_advisor,
          assigned_technician: values.assigned_technician,
        }),
      });

      if (response.ok) {
        const workOrderData = await response.json();
        const workOrderId = workOrderData.work_order?.id || workOrderData.id;

        // Create appointment for the work order
        try {
          const durationMinutes = values.duration_minutes || 120; // Default 2 hours
          const appointmentType = values.appointment_type || 'repair';

          await appointmentApi.create({
            work_order_id: workOrderId,
            estimate_id: estimate?.id,
            technician_id: selectedAppointmentSlot.technician_id,
            customer_id: estimate?.customer_id!,
            vehicle_id: estimate?.vehicle_id!,
            appointment_type: appointmentType,
            title: `Work Order #${workOrderData.work_order?.work_order_number || workOrderId}`,
            description: estimate?.internal_notes || estimate?.customer_notes || 'Work order from estimate conversion',
            start_time: selectedAppointmentSlot.start_time,
            end_time: selectedAppointmentSlot.end_time,
            duration_minutes: durationMinutes,
            travel_time_minutes: selectedAppointmentSlot.travel_time_minutes || 0,
            buffer_time_minutes: 15,
            status: 'scheduled',
            manual_override: false,
            has_conflict: false,
            conflict_acknowledged: false,
            waiting_on_parts: false,
            customer_notified: false,
            customer_confirmed: false,
            reminder_sent: false
          });

          message.success('Estimate converted to work order and appointment scheduled successfully');
        } catch (appointmentError) {
          console.error('Error creating appointment:', appointmentError);
          message.warning('Work order created but failed to schedule appointment. Please schedule manually.');
        }

        setConvertModalVisible(false);
        convertForm.resetFields();
        setSelectedAppointmentSlot(null);
        fetchEstimate(estimate?.id!);
      } else {
        const error = await response.json();
        message.error(error.error || 'Failed to convert estimate');
      }
    } catch (error: any) {
      console.error('Error converting estimate:', error);
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error('Failed to convert estimate');
      }
    }
  };

  const handleScheduleAppointment = () => {
    // Check if appointment already exists
    if (existingAppointment) {
      setRescheduleModalVisible(true);
      return;
    }

    // Always use smart booking - unified system for all estimates
    setSmartBookingModalVisible(true);
  };

  const handleAppointmentModalClose = () => {
    setAppointmentModalVisible(false);
    fetchEstimate(estimate?.id!);
    fetchExistingAppointment(estimate?.id!);
  };

  const handleSmartBookingClose = () => {
    setSmartBookingModalVisible(false);
    fetchEstimate(estimate?.id!);
    fetchExistingAppointment(estimate?.id!);
  };

  const handleReschedule = async () => {
    if (!rescheduleReason.trim()) {
      message.error('Please provide a reason for rescheduling');
      return;
    }

    try {
      // Cancel existing appointment with reason using the proper cancel endpoint
      await appointmentApi.cancel(
        existingAppointment.id!,
        1, // TODO: Get actual user ID from auth context
        `Rescheduling: ${rescheduleReason}`
      );

      // Clear state
      setRescheduleModalVisible(false);
      setRescheduleReason('');
      setExistingAppointment(null);

      message.success('Appointment cancelled. Please schedule a new appointment.');

      // Small delay to ensure backend has processed the cancellation
      setTimeout(() => {
        // Open smart booking modal
        setSmartBookingModalVisible(true);
      }, 500);
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);
      message.error('Failed to reschedule appointment');
    }
  };

  const handleDelete = async () => {
    if (!estimate) return;

    try {
      await estimateApi.delete(estimate.id!);
      message.success('Estimate deleted successfully');
      navigate(`${routePrefix}/estimates`);
    } catch (error: any) {
      console.error('Error deleting estimate:', error);
      console.error('Error response:', error?.response?.data);
      const errorMessage = error?.response?.data?.error || 'Failed to delete estimate';
      message.error(errorMessage);
    }
  };

  const handlePrint = () => {
    setPdfConfigModalVisible(true);
  };

  const handleDiscountEdit = (lineItemId: number, partId?: number) => {
    const key = partId ? `${lineItemId}-${partId}` : `${lineItemId}`;
    setEditingDiscounts(prev => ({ ...prev, [key]: true }));
    startEditing();
  };

  const handleDiscountSave = async (lineItemId: number, discountType: 'percentage' | 'amount' | 'final_price', value: number, partId?: number) => {
    if (!estimate) return;

    try {
      setSaving(true);
      const updatedEstimate = { ...estimate };

      if (partId) {
        // Update part discount
        const lineItem = updatedEstimate.line_items.find(item => item.id === lineItemId);
        if (lineItem && lineItem.parts) {
          const part = lineItem.parts.find(p => p.id === partId);
          if (part) {
            const basePrice = part.quantity * part.unit_price;
            if (discountType === 'percentage') {
              part.discount_percentage = value;
              part.discount_amount = undefined;
              part.discounted_price = basePrice - (basePrice * value / 100);
            } else if (discountType === 'final_price') {
              // "Set Final Price" — value IS the target price
              part.discount_amount = Math.max(0, basePrice - value);
              part.discount_percentage = undefined;
              part.discounted_price = value;
            } else {
              part.discount_amount = value;
              part.discount_percentage = undefined;
              part.discounted_price = basePrice - value;
            }
            part.total_price = part.discounted_price || (part.quantity * part.unit_price);
          }
        }
      } else {
        // Update labor discount
        const lineItem = updatedEstimate.line_items.find(item => item.id === lineItemId);
        if (lineItem && lineItem.labor_hours && lineItem.hourly_rate) {
          if (discountType === 'percentage') {
            lineItem.labor_discount_percentage = value;
            lineItem.labor_discount_amount = undefined;
          } else if (discountType === 'final_price') {
            const baseLaborCost = lineItem.labor_hours * lineItem.hourly_rate;
            lineItem.labor_discount_amount = Math.max(0, baseLaborCost - value);
            lineItem.labor_discount_percentage = undefined;
          } else {
            lineItem.labor_discount_amount = value;
            lineItem.labor_discount_percentage = undefined;
          }

          // Recalculate line item total
          const baseLaborCost = lineItem.labor_hours * lineItem.hourly_rate;
          const laborDiscountAmount = lineItem.labor_discount_amount ||
            (baseLaborCost * (lineItem.labor_discount_percentage || 0) / 100);
          const discountedLaborCost = baseLaborCost - laborDiscountAmount;

          const partsTotal = lineItem.parts?.reduce((sum, part) =>
            sum + (part.discounted_price || part.total_price), 0) || 0;

          lineItem.total_price = discountedLaborCost + partsTotal;
        }
      }

      // Recalculate totals from raw fields (labor hours*rate + parts qty*unit_price)
      const totals = calculateEstimateTotals(updatedEstimate.line_items, updatedEstimate.tax_rate || 0);

      updatedEstimate.subtotal = totals.discountedSubtotal;
      updatedEstimate.tax_amount = totals.taxAmount;
      updatedEstimate.total_amount = totals.total;

      // Update via API
      await estimateApi.update(estimate.id!, updatedEstimate);

      setEstimate(updatedEstimate);

      const key = partId ? `${lineItemId}-${partId}` : `${lineItemId}`;
      setEditingDiscounts(prev => ({ ...prev, [key]: false }));
      stopEditing();

      message.success('Discount updated successfully');
    } catch (error) {
      console.error('Error updating discount:', error);
      message.error('Failed to update discount');
      stopEditing();
    } finally {
      setSaving(false);
    }
  };

  const handleDiscountCancel = (lineItemId: number, partId?: number) => {
    const key = partId ? `${lineItemId}-${partId}` : `${lineItemId}`;
    setEditingDiscounts(prev => ({ ...prev, [key]: false }));
    stopEditing();
  };

  const DiscountEditor: React.FC<{
    lineItemId: number;
    partId?: number;
    originalPrice?: number;
    currentPercentage?: number;
    currentAmount?: number;
    onSave: (type: 'percentage' | 'amount' | 'final_price', value: number) => void;
    onCancel: () => void;
  }> = ({ lineItemId, partId, originalPrice, currentPercentage, currentAmount, onSave, onCancel }) => {
    const [discountType, setDiscountType] = useState<'percentage' | 'amount' | 'final_price'>(
      currentPercentage ? 'percentage' : 'amount'
    );
    const [value, setValue] = useState(
      currentPercentage || currentAmount || 0
    );

    const savingsDisplay = discountType === 'final_price' && originalPrice && value > 0
      ? `Customer saves: $${Math.max(0, originalPrice - value).toFixed(2)}`
      : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        <Select
          size="small"
          value={discountType}
          onChange={(val) => {
            setDiscountType(val);
            setValue(0);
          }}
          style={{ width: '75px' }}
        >
          <Select.Option value="percentage">%</Select.Option>
          <Select.Option value="amount">$</Select.Option>
          <Select.Option value="final_price">Final</Select.Option>
        </Select>
        <Input
          size="small"
          type="number"
          value={value}
          onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
          style={{ width: '80px' }}
          step={discountType === 'percentage' ? '0.1' : '0.01'}
          min="0"
          max={discountType === 'percentage' ? '100' : undefined}
          placeholder={discountType === 'final_price' ? 'Charge' : 'Discount'}
        />
        <Button
          size="small"
          type="primary"
          onClick={() => onSave(discountType, value)}
          loading={saving}
        >
          ✓
        </Button>
        <Button
          size="small"
          onClick={onCancel}
        >
          ✕
        </Button>
        {savingsDisplay && (
          <span style={{ fontSize: '10px', color: '#10b981', whiteSpace: 'nowrap' }}>{savingsDisplay}</span>
        )}
      </div>
    );
  };

  const partColumns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => `$${(price || 0).toFixed(2)}`,
    },
    {
      title: 'Discount',
      key: 'discount',
      width: 140,
      align: 'right' as const,
      render: (value: any, record: any, index: number) => {
        // Find the line item this part belongs to
        const lineItem = estimate?.line_items.find(item =>
          item.parts?.some(part => part.id === record.id)
        );

        if (!lineItem) return '—';

        const key = `${lineItem.id}-${record.id}`;
        const isEditing = editingDiscounts[key];

        if (isEditing) {
          return (
            <DiscountEditor
              lineItemId={lineItem.id!}
              partId={record.id}
              originalPrice={record.quantity * record.unit_price}
              currentPercentage={record.discount_percentage}
              currentAmount={record.discount_amount}
              onSave={(type, value) => handleDiscountSave(lineItem.id!, type, value, record.id)}
              onCancel={() => handleDiscountCancel(lineItem.id!, record.id)}
            />
          );
        }

        const hasDiscount = record.discount_percentage || record.discount_amount;
        const canEdit = estimate?.status !== 'converted';

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
            {hasDiscount ? (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                -{record.discount_percentage
                  ? `${record.discount_percentage}%`
                  : `$${(record.discount_amount || 0).toFixed(2)}`}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
            )}
            {canEdit && (
              <Tooltip title="Edit discount">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleDiscountEdit(lineItem.id!, record.id)}
                  style={{ padding: '0 4px', fontSize: '10px' }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      key: 'part_total',
      width: 100,
      align: 'right' as const,
      render: (record: any) => {
        const originalTotal = record.total_price || 0;
        const discountedPrice = record.discounted_price;
        const hasDiscount = record.discount_percentage || record.discount_amount;

        return (
          <div style={{ textAlign: 'right' }}>
            {hasDiscount && discountedPrice !== undefined ? (
              <>
                <div style={{ textDecoration: 'line-through', fontSize: '11px', color: '#999' }}>
                  ${originalTotal.toFixed(2)}
                </div>
                <Text style={{ color: '#10b981' }}>
                  ${discountedPrice.toFixed(2)}
                </Text>
              </>
            ) : (
              <Text style={{ color: '#10b981' }}>
                ${originalTotal.toFixed(2)}
              </Text>
            )}
          </div>
        );
      },
    },
  ];

  const lineItemColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: '500' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.category}</Text>
          {record.notes && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {record.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'category',
      key: 'type',
      width: 100,
      render: (category: string, record: any) => {
        // Determine type based on line item properties
        const type = record.labor_hours && record.hourly_rate ? 'labor' :
          record.parts && record.parts.length > 0 ? 'parts' :
            'service';
        return (
          <Tag color={type === 'labor' ? 'blue' : type === 'parts' ? 'green' : 'orange'}>
            {(type || 'service').toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 180,
      align: 'center' as const,
      render: (record: any) => {
        if (!record.labor_hours || !record.hourly_rate) return '—';

        const baseLaborCost = record.labor_hours * record.hourly_rate;
        const hasDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        const laborDiscountAmount = record.labor_discount_amount ||
          (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;
        const key = `${record.id}`;
        const isEditing = editingDiscounts[key];
        const canEdit = estimate?.status !== 'converted';

        if (isEditing) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{ fontSize: '12px' }}>{record.labor_hours}h @ ${record.hourly_rate}/hr</div>
              <DiscountEditor
                lineItemId={record.id!}
                originalPrice={(record.labor_hours || 0) * (record.hourly_rate || 0)}
                currentPercentage={record.labor_discount_percentage}
                currentAmount={record.labor_discount_amount}
                onSave={(type, value) => handleDiscountSave(record.id!, type, value)}
                onCancel={() => handleDiscountCancel(record.id!)}
              />
            </div>
          );
        }

        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              {record.labor_hours}h @ ${record.hourly_rate}/hr
            </div>
            {hasDiscount ? (
              <div style={{
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px',
                padding: '4px 6px',
                marginBottom: '4px'
              }}>
                <div style={{
                  fontSize: '11px',
                  textDecoration: 'line-through',
                  color: '#8c8c8c'
                }}>
                  Original: ${baseLaborCost.toFixed(2)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#ef4444',
                  fontWeight: '600'
                }}>
                  Discount: -{record.labor_discount_percentage
                    ? `${record.labor_discount_percentage}%`
                    : `$${(record.labor_discount_amount || 0).toFixed(2)}`}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#10b981',
                  fontWeight: '600'
                }}>
                  Final: ${discountedLaborCost.toFixed(2)}
                </div>
              </div>
            ) : (
              <div style={{
                fontSize: '12px',
                color: '#10b981',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                Total: ${baseLaborCost.toFixed(2)}
              </div>
            )}
            {canEdit && (
              <Tooltip title="Edit labor discount">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleDiscountEdit(record.id!)}
                  style={{ padding: '0 4px', fontSize: '10px' }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Parts',
      key: 'parts_count',
      width: 80,
      align: 'center' as const,
      render: (record: any) => {
        const partsCount = record.parts?.length || 0;
        return partsCount > 0 ? (
          <Tag color="blue">{partsCount} part{partsCount !== 1 ? 's' : ''}</Tag>
        ) : '—';
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      align: 'right' as const,
      render: (total: number, record: any) => {
        const hasLaborDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        const hasPartDiscount = record.parts?.some((part: any) => part.discount_percentage || part.discount_amount);

        return (
          <div style={{ textAlign: 'right' }}>
            <Text strong style={{ color: '#10b981' }}>
              ${(total || 0).toFixed(2)}
            </Text>
            {(hasLaborDiscount || hasPartDiscount) && (
              <div style={{ fontSize: '10px', color: '#ef4444' }}>
                Discounted
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading || !estimate) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text>Loading estimate details...</Text>
      </div>
    );
  }

  const canEdit = estimate.status !== 'converted';
  const canConvert = estimate.status === 'approved';
  const canSchedule = estimate.status !== 'converted';

  return (
    <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="estimate-detail-header">
        <Space align="center" className="estimate-detail-title-section">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`${routePrefix}/estimates`)}
          >
            Back to Estimates
          </Button>
          <Divider type="vertical" />
          <Title level={2} className="estimate-detail-title">
            {estimate.estimate_number}
          </Title>
          <Tag
            icon={getStatusIcon(estimate.status)}
            color={getStatusColor(estimate.status)}
            style={{
              fontSize: '16px',
              padding: '8px 20px',
              fontWeight: '600',
              borderRadius: '8px',
              border: `2px solid ${getStatusColor(estimate.status)}`,
              boxShadow: `0 2px 8px ${getStatusColor(estimate.status)}33`
            }}
          >
            {getStatusText(estimate.status).toUpperCase()}
          </Tag>
        </Space>

        {/* Action Buttons */}
        <Space wrap className="estimate-detail-actions">
          {estimate.public_token && (
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={async () => {
                const url = `${window.location.origin}/estimate/${estimate.public_token}`;
                const success = await copyToClipboard(url);
                if (success) {
                  message.success('Link copied!');
                } else {
                  message.error('Failed to copy link');
                }
              }}
            >
              Copy Link
            </Button>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Switch
              size="small"
              checked={estimate.public_link_active !== false}
              onChange={(checked) => {
                estimateApi.togglePublicLink(estimate.id!, checked);
                setEstimate({ ...estimate, public_link_active: checked });
              }}
            />
            <span style={{ fontSize: '12px', color: '#666' }}>
              Customer Access {estimate.public_link_active !== false ? 'ON' : 'OFF'}
            </span>
          </span>
          {canEdit && (
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`edit`)}
            >
              Edit Estimate
            </Button>
          )}
          {canConvert && (
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={() => setConvertModalVisible(true)}
            >
              Convert to Work Order
            </Button>
          )}
          {canSchedule && (
            <Button
              icon={<CalendarOutlined />}
              onClick={handleScheduleAppointment}
              type={existingAppointment ? 'default' : 'primary'}
            >
              {existingAppointment ? 'Reschedule Appointment' : 'Schedule Appointment'}
            </Button>
          )}
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print / PDF
          </Button>
          <PartsCatalogButton
            vehicle={estimate.vehicle}
            showText={true}
          />
          {(estimate.status === 'draft' || estimate.status === 'sent' || estimate.status === 'unapproved') && (
            <Button
              type={estimate.status === 'draft' ? 'primary' : 'default'}
              icon={<SendOutlined />}
              onClick={() => setSendEstimateModalVisible(true)}
            >
              {estimate.status === 'draft' ? 'Send to Customer' : 'Resend to Customer'}
            </Button>
          )}
          <Popconfirm
            title="Are you sure you want to delete this estimate?"
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Status Alert */}
      {estimate.status === 'converted' && (
        <Alert
          message="This estimate has been converted to a work order"
          description="No further changes can be made to this estimate."
          type="info"
          icon={<SwapOutlined />}
          style={{ marginBottom: '24px' }}
          showIcon
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Main Content */}
        <Col xs={24} lg={16}>
          {/* Estimate Information */}
          <Card title="Estimate Details" style={{ marginBottom: '24px' }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="middle">
              <Descriptions.Item label="Estimate Number">
                <Text strong>{estimate.estimate_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(estimate.created_at).format('MMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Valid Until">
                {estimate.valid_until
                  ? dayjs(estimate.valid_until).format('MMM DD, YYYY')
                  : 'No expiration date'
                }
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  icon={getStatusIcon(estimate.status)}
                  color={getStatusColor(estimate.status)}
                >
                  {getStatusText(estimate.status)}
                </Tag>
              </Descriptions.Item>
              {estimate.customer_notes && (
                <Descriptions.Item label="Customer Notes" span={2}>
                  {estimate.customer_notes}
                </Descriptions.Item>
              )}
              {estimate.customer_notes && (
                <Descriptions.Item label="Customer Notes" span={2}>
                  {estimate.customer_notes}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Service Items */}
          <Card title={`Service Items (${estimate.line_items.length})`}>
            <Table
              columns={lineItemColumns}
              dataSource={estimate.line_items}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  if (!record.parts || record.parts.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                        No parts required for this service
                      </div>
                    );
                  }
                  return (
                    <div style={{ margin: 0 }}>
                      <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                        Parts Required:
                      </Text>
                      <Table
                        columns={partColumns}
                        dataSource={record.parts}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        showHeader={true}
                        style={{ marginLeft: '24px' }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => record.parts && record.parts.length > 0,
              }}
              footer={() => {
                // Calculate discount breakdown
                const totalDiscounts = estimate.line_items.reduce((sum, item) => {
                  let itemDiscounts = 0;

                  // Labor discounts
                  if (item.labor_hours && item.hourly_rate) {
                    const baseLaborCost = item.labor_hours * item.hourly_rate;
                    const laborDiscountAmount = item.labor_discount_amount ||
                      (baseLaborCost * (item.labor_discount_percentage || 0) / 100);
                    itemDiscounts += laborDiscountAmount;
                  }

                  // Part discounts
                  if (item.parts) {
                    const partDiscounts = item.parts.reduce((partSum, part) => {
                      const basePartCost = part.quantity * part.unit_price;
                      const partDiscountAmount = part.discount_amount ||
                        (basePartCost * (part.discount_percentage || 0) / 100);
                      return partSum + partDiscountAmount;
                    }, 0);
                    itemDiscounts += partDiscounts;
                  }

                  return sum + itemDiscounts;
                }, 0);

                const originalSubtotal = (estimate.subtotal || 0) + totalDiscounts;

                return (
                  <div style={{ textAlign: 'right' }}>
                    <Space direction="vertical" size="small">
                      {totalDiscounts > 0 && (
                        <>
                          <div>
                            <Text type="secondary">Original Subtotal: </Text>
                            <Text type="secondary">${originalSubtotal.toFixed(2)}</Text>
                          </div>
                          <div>
                            <Text type="secondary">Total Discounts: </Text>
                            <Text style={{ color: '#ef4444' }}>-${totalDiscounts.toFixed(2)}</Text>
                          </div>
                        </>
                      )}
                      <div>
                        <Text>Subtotal: </Text>
                        <Text strong>${(estimate.subtotal || 0).toFixed(2)}</Text>
                      </div>
                      <div>
                        <Text>Tax ({estimate.tax_rate || 0}%): </Text>
                        <Text strong>${(estimate.tax_amount || 0).toFixed(2)}</Text>
                      </div>
                      <Divider style={{ margin: '8px 0' }} />
                      <div>
                        <Text strong style={{ fontSize: '18px' }}>Total: </Text>
                        <Text strong style={{ fontSize: '18px', color: '#10b981' }}>
                          ${(estimate.total_amount || 0).toFixed(2)}
                        </Text>
                        {totalDiscounts > 0 && (
                          <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '2px' }}>
                            (You saved ${totalDiscounts.toFixed(2)})
                          </div>
                        )}
                      </div>
                    </Space>
                  </div>
                );
              }}
            />
          </Card>
        </Col>

        {/* Sidebar */}
        <Col xs={24} lg={8}>
          {/* Activity Log */}
          <div style={{ marginBottom: '24px' }}>
            <ActivityLogComponent
              entityType="estimate"
              entityId={estimate.id!}
              height="400px"
            />
          </div>

          {/* Customer Information */}
          <Card
            title="Customer Information"
            style={{ marginBottom: '24px' }}
            extra={
              <Avatar
                size="small"
                style={{ backgroundColor: '#3b82f6' }}
                icon={<UserOutlined />}
              />
            }
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Name</Text>
                <Text strong>
                  {estimate.customer?.company_name ||
                    `${estimate.customer?.first_name} ${estimate.customer?.last_name}`}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Phone</Text>
                <Text>{estimate.customer?.phone}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Email</Text>
                <Text>{estimate.customer?.email}</Text>
              </div>
              {estimate.customer?.address && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Address</Text>
                  <Text>
                    {estimate.customer.address}<br />
                    {estimate.customer.city}, {estimate.customer.state} {estimate.customer.zip}
                  </Text>
                </div>
              )}
            </Space>
          </Card>

          {/* Vehicle Information */}
          <Card
            title="Vehicle Information"
            style={{ marginBottom: '24px' }}
            extra={
              <Avatar
                size="small"
                style={{ backgroundColor: '#f59e0b' }}
                icon={<CarOutlined />}
              />
            }
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Vehicle</Text>
                <Text strong>
                  {estimate.vehicle?.year} {estimate.vehicle?.make} {estimate.vehicle?.model}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Mileage</Text>
                <Text>{(estimate.vehicle?.current_mileage || 0).toLocaleString()} miles</Text>
              </div>
              {estimate.vehicle?.license_plate && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>License Plate</Text>
                  <Text>{estimate.vehicle.license_plate}</Text>
                </div>
              )}
              {estimate.vehicle?.vin && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>VIN</Text>
                  <Text style={{ fontSize: '12px' }}>{estimate.vehicle.vin}</Text>
                </div>
              )}
              {estimate.vehicle?.color && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Color</Text>
                  <Text>{estimate.vehicle.color}</Text>
                </div>
              )}
            </Space>
          </Card>

          {/* Appointment Information */}
          {existingAppointment && (
            <Card
              title="Scheduled Appointment"
              extra={
                <Avatar
                  size="small"
                  style={{ backgroundColor: '#52c41a' }}
                  icon={<CalendarOutlined />}
                />
              }
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  message="Appointment Scheduled"
                  description={
                    <div>
                      <div style={{ marginTop: '8px' }}>
                        <Text strong>{dayjs(existingAppointment.start_time).format('MMMM D, YYYY')}</Text>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <ClockCircleOutlined style={{ marginRight: '8px' }} />
                        <Text>{dayjs(existingAppointment.start_time).format('h:mm A')} - {dayjs(existingAppointment.end_time).format('h:mm A')}</Text>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <UserOutlined style={{ marginRight: '8px' }} />
                        <Text>
                          {users.find(u => u.id === existingAppointment.technician_id)?.first_name}{' '}
                          {users.find(u => u.id === existingAppointment.technician_id)?.last_name}
                        </Text>
                      </div>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: '12px' }}
                />
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Status</Text>
                  <Tag color={
                    existingAppointment.status === 'scheduled' ? 'blue' :
                      existingAppointment.status === 'completed' ? 'green' :
                        existingAppointment.status === 'cancelled' ? 'red' : 'default'
                  }>
                    {existingAppointment.status?.toUpperCase()}
                  </Tag>
                </div>
                {existingAppointment.notes && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Notes</Text>
                    <Text style={{ fontSize: '13px' }}>{existingAppointment.notes}</Text>
                  </div>
                )}
              </Space>
            </Card>
          )}

          {/* SMS Conversation */}
          {estimate.customer?.phone && (
            <SMSConversationThread
              estimateId={estimate.id}
              customerId={estimate.customer.id}
              customerPhone={estimate.customer.phone}
              height={400}
            />
          )}
        </Col>
      </Row>

      {/* Disclaimer Section */}
      {estimate.disclaimer && (
        <Card title="Important Terms & Conditions" style={{ marginTop: '24px' }}>
          <Alert
            message="Please Read Carefully"
            description={estimate.disclaimer}
            type="warning"
            showIcon
            style={{
              border: '2px solid #d97706',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              fontSize: '14px',
              lineHeight: '1.6'
            }}
          />
        </Card>
      )}

      {/* Convert to Work Order Modal */}
      <Modal
        title="Convert to Work Order & Schedule Appointment"
        open={convertModalVisible}
        onOk={handleConvertToWorkOrder}
        onCancel={() => {
          setConvertModalVisible(false);
          convertForm.resetFields();
          setSelectedAppointmentSlot(null);
        }}
        okText="Convert & Schedule"
        okButtonProps={{ type: 'primary', disabled: !selectedAppointmentSlot }}
        centered
        width={window.innerWidth < 500 ? '95vw' : 900}
      >
        <div style={{ marginBottom: '16px' }}>
          <Alert
            message="Convert Estimate to Work Order"
            description="This will create a new work order with all the services from this estimate and schedule an appointment. Both appointment selection and work order details are required."
            type="info"
            showIcon
          />
        </div>
        <Form form={convertForm} layout="vertical" initialValues={{ priority: 'normal', appointment_type: 'repair', duration_minutes: 120 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Priority" name="priority">
                <Select>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="normal">Normal</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="urgent">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Appointment Type" name="appointment_type">
                <Select>
                  <Select.Option value="diagnosis">Diagnosis</Select.Option>
                  <Select.Option value="repair">Repair</Select.Option>
                  <Select.Option value="inspection">Inspection</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Assign Advisor" name="assigned_advisor">
                <Select
                  placeholder="Select an advisor"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={users
                    .filter(user => user.role === 'owner' || user.role === 'advisor')
                    .map(user => ({
                      value: `${user.first_name} ${user.last_name}`,
                      label: `${user.first_name} ${user.last_name} (${user.role})`,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Estimated Completion" name="estimated_completion">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Estimated Duration (minutes)" name="duration_minutes">
            <Select>
              <Select.Option value={30}>30 minutes</Select.Option>
              <Select.Option value={60}>1 hour</Select.Option>
              <Select.Option value={90}>1.5 hours</Select.Option>
              <Select.Option value={120}>2 hours</Select.Option>
              <Select.Option value={180}>3 hours</Select.Option>
              <Select.Option value={240}>4 hours</Select.Option>
              <Select.Option value={360}>6 hours</Select.Option>
              <Select.Option value={480}>8 hours (Full day)</Select.Option>
            </Select>
          </Form.Item>

          <Divider>Select Appointment Time</Divider>

          <Form.Item>
            <AvailabilityPicker
              appointmentType={convertForm.getFieldValue('appointment_type') || 'repair'}
              customerId={estimate?.customer_id}
              vehicleId={estimate?.vehicle_id}
              estimatedDuration={convertForm.getFieldValue('duration_minutes') || 120}
              onSelectSlot={setSelectedAppointmentSlot}
              selectedSlot={selectedAppointmentSlot}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Old Enhanced Appointment Modal - REMOVED - Now using unified SmartAppointmentBooking for all estimates */}
      {/* {estimate && (
        <EnhancedAppointmentModal
          visible={appointmentModalVisible}
          appointment={null}
          estimateId={estimate.id}
          onClose={handleAppointmentModalClose}
        />
      )} */}

      {/* PDF Configuration Modal */}
      {estimate && (
        <PDFConfigModal
          visible={pdfConfigModalVisible}
          estimate={estimate}
          onClose={() => setPdfConfigModalVisible(false)}
        />
      )}

      {/* Send Estimate Modal */}
      {estimate && (
        <SendEstimateModal
          visible={sendEstimateModalVisible}
          onClose={() => setSendEstimateModalVisible(false)}
          estimateId={estimate.id!}
          estimateNumber={estimate.estimate_number}
          customerEmail={estimate.customer?.email}
          customerPhone={estimate.customer?.phone}
          onSent={() => {
            setSendEstimateModalVisible(false);
            fetchEstimate(estimate.id!);
          }}
        />
      )}

      {/* Smart Appointment Booking Modal - Unified Booking System */}
      {estimate && (
        <SmartAppointmentBooking
          visible={smartBookingModalVisible}
          estimateId={estimate.id!}
          onClose={handleSmartBookingClose}
          onSuccess={handleSmartBookingClose}
        />
      )}

      {/* Reschedule Modal */}
      <Modal
        title="Reschedule Appointment"
        open={rescheduleModalVisible}
        onOk={handleReschedule}
        onCancel={() => {
          setRescheduleModalVisible(false);
          setRescheduleReason('');
        }}
        okText="Reschedule"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="Rescheduling Appointment"
          description="The current appointment will be cancelled. You will then be able to select a new time slot."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        {existingAppointment && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Current Appointment:</Text>
            <div style={{ marginTop: '8px' }}>
              <Text>{dayjs(existingAppointment.start_time).format('MMMM D, YYYY [at] h:mm A')}</Text>
            </div>
          </div>
        )}
        <div>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Reason for Rescheduling *</Text>
          <TextArea
            rows={4}
            placeholder="Please provide a reason for rescheduling this appointment..."
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </div>
  );
};

export default EstimateDetail;